/**
 * Unified Responsive System for Poker Reborn
 *
 * Single source of truth for all responsive scaling using Breakpoint-Based Scaling (BBS).
 * One system with two modes: interpolation (smooth) and discrete (jumps at breakpoints).
 *
 * @see docs/systems/BBS.md - Full documentation
 * @see tasks/active/T20_BBS_DISCRETE_MODE_PLAN.md - Implementation plan
 */

import type { DeviceType } from '@/types/ui';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Options for getScaledValue() behavior.
 */
export interface ScalingOptions {
  /**
   * If true, returns the exact breakpoint value (floor-based, no interpolation).
   * If false, linearly interpolates between breakpoints.
   */
  discrete: boolean;
}

// =============================================================================
// DEVICE TYPE THRESHOLDS
// =============================================================================

/**
 * Screen width thresholds for device type classification.
 * These are the boundaries where discrete UI changes occur.
 */
export const DEVICE_THRESHOLDS = {
  /** Below this width = phone */
  phone: 480,
  /** Below this width (but >= phone) = tablet */
  tablet: 1024,
  // desktop: >= 1024px
} as const;

// =============================================================================
// DEVICE TYPE
// =============================================================================

/**
 * Get device type from screen width.
 * Use for discrete UI changes (fonts, padding, layout mode).
 *
 * @example
 * const deviceType = getDeviceType(800); // 'tablet'
 *
 * @param screenWidth - Current screen width in pixels
 * @returns DeviceType classification
 */
export function getDeviceType(screenWidth: number): DeviceType {
  if (screenWidth < DEVICE_THRESHOLDS.phone) return 'phone';
  if (screenWidth < DEVICE_THRESHOLDS.tablet) return 'tablet';
  return 'desktop';
}

// =============================================================================
// BREAKPOINT-BASED SCALING (BBS)
// =============================================================================

/**
 * Get a scaled value based on screen width and breakpoints.
 *
 * Two modes:
 * - `{ discrete: false }` - Linearly interpolates between breakpoints (smooth scaling)
 * - `{ discrete: true }` - Returns exact breakpoint value, no interpolation (jumps)
 *
 * @example
 * // Smooth scaling for card sizes
 * const cardWidth = getScaledValue(screenWidth, {
 *   1920: 84,  // Large desktop
 *   1440: 63,  // Desktop
 *   720: 40,   // Tablet
 *   375: 35,   // Mobile
 * }, { discrete: false });
 *
 * @example
 * // Discrete jumps for font sizes
 * const fontSize = getScaledValue(screenWidth, {
 *   1920: 16,
 *   1440: 14,
 *   720: 12,
 *   375: 10,
 * }, { discrete: true });
 *
 * @param screenWidth - Current screen width in pixels
 * @param breakpoints - Map of screen widths to desired values
 * @param options - Scaling behavior options
 * @returns Scaled value for current screen width
 */
export function getScaledValue(
  screenWidth: number,
  breakpoints: Record<number, number>,
  options: ScalingOptions
): number {
  const sortedBreakpoints = Object.keys(breakpoints)
    .map(Number)
    .sort((a, b) => a - b);

  if (sortedBreakpoints.length === 0) {
    throw new Error('Breakpoints map cannot be empty');
  }

  const smallest = sortedBreakpoints[0]!;
  const largest = sortedBreakpoints[sortedBreakpoints.length - 1]!;

  // Discrete mode: find floor breakpoint and return its value
  if (options.discrete) {
    // Below smallest → use smallest value
    if (screenWidth < smallest) {
      return breakpoints[smallest]!;
    }
    // At or above largest → use largest value
    if (screenWidth >= largest) {
      return breakpoints[largest]!;
    }
    // Find the largest breakpoint <= screenWidth (floor)
    let floorBreakpoint = smallest;
    for (const bp of sortedBreakpoints) {
      if (bp <= screenWidth) {
        floorBreakpoint = bp;
      } else {
        break;
      }
    }
    return breakpoints[floorBreakpoint]!;
  }

  // Interpolation mode (discrete: false)

  // Below smallest breakpoint → use smallest value
  if (screenWidth <= smallest) {
    return breakpoints[smallest]!;
  }

  // Above largest breakpoint → extrapolate from last two
  if (screenWidth >= largest) {
    if (sortedBreakpoints.length === 1) {
      return breakpoints[largest]!;
    }
    const secondLargest = sortedBreakpoints[sortedBreakpoints.length - 2]!;
    const ratio = (screenWidth - secondLargest) / (largest - secondLargest);
    return (
      breakpoints[secondLargest]! +
      (breakpoints[largest]! - breakpoints[secondLargest]!) * ratio
    );
  }

  // Between breakpoints → interpolate
  for (let i = 0; i < sortedBreakpoints.length - 1; i++) {
    const low = sortedBreakpoints[i]!;
    const high = sortedBreakpoints[i + 1]!;
    if (screenWidth >= low && screenWidth <= high) {
      const ratio = (screenWidth - low) / (high - low);
      return breakpoints[low]! + (breakpoints[high]! - breakpoints[low]!) * ratio;
    }
  }

  return breakpoints[smallest]!; // Fallback (should never reach)
}

// =============================================================================
// CONVENIENCE HELPERS
// =============================================================================

/**
 * Get a value from a DeviceType lookup table.
 * Combines getDeviceType() + table lookup in one call.
 *
 * @example
 * const fontSize = getDeviceValue(screenWidth, {
 *   desktop: 16,
 *   tablet: 14,
 *   phone: 12,
 * });
 *
 * @param screenWidth - Current screen width in pixels
 * @param values - Map of DeviceType to values
 * @returns Value for current device type
 */
export function getDeviceValue<T>(
  screenWidth: number,
  values: Record<DeviceType, T>
): T {
  const deviceType = getDeviceType(screenWidth);
  return values[deviceType];
}

/**
 * Check if current screen width is considered mobile (phone).
 *
 * @param screenWidth - Current screen width in pixels
 * @returns true if phone-sized
 */
export function isMobile(screenWidth: number): boolean {
  return screenWidth < DEVICE_THRESHOLDS.phone;
}

/**
 * Check if current screen width is considered tablet.
 *
 * @param screenWidth - Current screen width in pixels
 * @returns true if tablet-sized
 */
export function isTablet(screenWidth: number): boolean {
  return (
    screenWidth >= DEVICE_THRESHOLDS.phone && screenWidth < DEVICE_THRESHOLDS.tablet
  );
}

/**
 * Check if current screen width is considered desktop.
 *
 * @param screenWidth - Current screen width in pixels
 * @returns true if desktop-sized
 */
export function isDesktop(screenWidth: number): boolean {
  return screenWidth >= DEVICE_THRESHOLDS.tablet;
}
